import React, { useState } from 'react'
import { useParams } from '@cedarjs/router'
import { useQuery, useMutation, gql } from '@apollo/client'
import { toast } from 'react-hot-toast'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Paper,
  Alert,
  CircularProgress,
  Checkbox,
  Drawer,
  IconButton,
} from '@mui/material'
import { Assignment, Close } from '@mui/icons-material'

import { useOrgName } from 'src/hooks/useOrgName'
import { routes } from '@cedarjs/router'
import { GET_EXTERNAL_WORK_ORDER } from './queries'

const UPDATE_EXTERNAL_WORK_ORDER = gql`
  mutation UpdateExternalWorkOrder(
    $id: String!
    $input: UpdateExternalWorkOrderInput!
  ) {
    updateExternalWorkOrder(id: $id, input: $input) {
      id
      usageLogId
    }
  }
`
import WorkOrderStatusChip from 'src/components/WorkOrderStatusChip'
import WorkItemStatusChip from 'src/components/WorkItemStatusChip'
import WorkItemsTabbedTable from 'src/components/WorkItemsTabbedTable'
import WorkItemBulkOperations, {
  BULK_UPDATE_EXTERNAL_WORK_ITEMS,
} from 'src/components/WorkItemBulkOperations'
import ExternalAircraftTimeModal from 'src/components/ExternalAircraftTimeModal/ExternalAircraftTimeModal'

const ExternalWorkOrderDetailPage = () => {
  const { id, orgName } = useParams()
  const orgSlug = useOrgName()

  const [bulkOperationsOpen, setBulkOperationsOpen] = useState(false)
  const [selectedWorkItems, setSelectedWorkItems] = useState<string[]>([])
  const [aircraftDetailsOpen, setAircraftDetailsOpen] = useState(false)
  const [applyTimesOpen, setApplyTimesOpen] = useState(false)
  const [reviewOpen, setReviewOpen] = useState(false)

  // Query for work order data
  const { data, loading, error, refetch } = useQuery(GET_EXTERNAL_WORK_ORDER, {
    variables: { orgSlug: orgName, id },
    skip: !id || !orgName,
    errorPolicy: 'all',
  })

  // Mutation for bulk operations
  const [bulkUpdate, { loading: bulkUpdateLoading }] = useMutation(
    BULK_UPDATE_EXTERNAL_WORK_ITEMS,
    {
      onCompleted: () => {
        refetch()
      },
      onError: (error) => {
        toast.error('Failed to update work items: ' + error.message)
      },
    }
  )

  // Mutation for updating work order
  const [updateWorkOrder] = useMutation(UPDATE_EXTERNAL_WORK_ORDER)

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
        <Box ml={2}>
          <Typography variant="body2">
            Loading... orgName={orgName}, id={id}
          </Typography>
        </Box>
      </Box>
    )
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Error loading work order: {error.message}
        </Alert>
        <Box mt={2}>
          <Typography variant="body2">
            Debug info: orgName={orgName}, id={id}
          </Typography>
        </Box>
      </Box>
    )
  }

  if (!data?.externalWorkOrder) {
    return (
      <Box p={3}>
        <Alert severity="warning">Work order not found</Alert>
        <Box mt={2}>
          <Typography variant="body2">
            Debug info: orgName={orgName}, id={id}
          </Typography>
          <Typography variant="body2">
            GraphQL data: {JSON.stringify(data, null, 2)}
          </Typography>
          <Typography variant="body2">
            GraphQL error: {error ? JSON.stringify(error, null, 2) : 'No error'}
          </Typography>
        </Box>
      </Box>
    )
  }

  const workOrder = data.externalWorkOrder

  return (
    <Box p={3}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box>
          <Typography variant="h4" gutterBottom>
            Work Order: {workOrder.workOrderNumber}
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <WorkOrderStatusChip status={workOrder.status} size="medium" />
            <Typography variant="subtitle1" color="textSecondary">
              {workOrder.description || 'No description'}
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            onClick={() => setAircraftDetailsOpen(true)}
          >
            Aircraft Details
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setApplyTimesOpen(true)}
          >
            Apply Times
          </Button>
          <Button variant="outlined" onClick={() => setReviewOpen(true)}>
            Review
          </Button>
        </Box>
      </Box>

      {/* Status and Basic Info */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Status & Details
              </Typography>
              <Box display="flex" alignItems="center" gap={2} mb={2}>
                <WorkOrderStatusChip status={workOrder.status} />
                <Typography variant="body2" color="textSecondary">
                  Created: {new Date(workOrder.createdAt).toLocaleDateString()}
                </Typography>
              </Box>
              {workOrder.startedAt && (
                <Typography variant="body2" color="textSecondary" mb={1}>
                  Started: {new Date(workOrder.startedAt).toLocaleDateString()}
                </Typography>
              )}
              {workOrder.completedAt && (
                <Typography variant="body2" color="textSecondary" mb={1}>
                  Completed:{' '}
                  {new Date(workOrder.completedAt).toLocaleDateString()}
                </Typography>
              )}
              {workOrder.assignee && (
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <Assignment fontSize="small" />
                  <Typography variant="body2">
                    Assigned to: {workOrder.assignee.firstName}{' '}
                    {workOrder.assignee.lastName}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Aircraft Information
              </Typography>
              <Typography variant="body2" mb={1}>
                <strong>Tail Number:</strong> {workOrder.aircraft.tailNumber}
              </Typography>
              <Typography variant="body2" mb={1}>
                <strong>Model:</strong> {workOrder.aircraft.model}
              </Typography>
              <Typography variant="body2" mb={1}>
                <strong>Make:</strong>{' '}
                {workOrder.aircraft.make || 'Not specified'}
              </Typography>
              <Typography variant="body2" mb={1}>
                <strong>Serial Number:</strong>{' '}
                {workOrder.aircraft.serialNumber || 'Not specified'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Customer Information */}
      {workOrder.customer && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Customer Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" mb={1}>
                  <strong>Name:</strong> {workOrder.customer.name}
                </Typography>
                <Typography variant="body2" mb={1}>
                  <strong>Email:</strong>{' '}
                  {workOrder.customer.email || 'Not provided'}
                </Typography>
                <Typography variant="body2" mb={1}>
                  <strong>Phone:</strong>{' '}
                  {workOrder.customer.phoneNumber || 'Not provided'}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Work Items */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Work Items
          </Typography>
          {workOrder.workItems.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              No work items found
            </Typography>
          ) : (
            <WorkItemsTabbedTable
              workItems={workOrder.workItems}
              selectedWorkItems={selectedWorkItems}
              onWorkItemSelectionChange={(workItemId, selected) => {
                if (selected) {
                  setSelectedWorkItems([...selectedWorkItems, workItemId])
                } else {
                  setSelectedWorkItems(
                    selectedWorkItems.filter((id) => id !== workItemId)
                  )
                }
              }}
              onBulkOperationsClick={() => setBulkOperationsOpen(true)}
            />
          )}
        </CardContent>
      </Card>

      {/* Notes */}
      {workOrder.notes && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Notes
            </Typography>
            <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
              {workOrder.notes}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Bulk Operations Dialog */}
      <WorkItemBulkOperations
        open={bulkOperationsOpen}
        onClose={() => setBulkOperationsOpen(false)}
        selectedWorkItems={workOrder.workItems.filter((item) =>
          selectedWorkItems.includes(item.id)
        )}
        onSuccess={() => {
          refetch()
          setSelectedWorkItems([])
        }}
      />

      {/* Aircraft Details Sidebar */}
      <Drawer
        open={aircraftDetailsOpen}
        onClose={() => setAircraftDetailsOpen(false)}
        anchor="right"
        aria-labelledby="aircraft-details-title"
        aria-describedby="aircraft-details-description"
        disablePortal
        sx={{ zIndex: 1300 }}
        PaperProps={{
          className: 'min-w-[40%] flex flex-col',
        }}
      >
        <div className="flex items-center justify-between border-b border-gray-200 bg-white p-4 text-gray-900">
          <Typography variant="h6">Aircraft Details</Typography>
          <IconButton
            onClick={() => setAircraftDetailsOpen(false)}
            className="text-gray-600"
          >
            <Close />
          </IconButton>
        </div>
        <Divider />

        {/* Content Area - Scrollable */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Aircraft Information Section */}
            <div>
              <Typography variant="h6" gutterBottom>
                Aircraft Information
              </Typography>
              <div className="space-y-3">
                <div>
                  <Typography variant="body2" fontWeight="medium">
                    Tail Number
                  </Typography>
                  <Typography variant="body1">
                    {workOrder.aircraft.tailNumber}
                  </Typography>
                </div>
                <div>
                  <Typography variant="body2" fontWeight="medium">
                    Model
                  </Typography>
                  <Typography variant="body1">
                    {workOrder.aircraft.model}
                  </Typography>
                </div>
                <div>
                  <Typography variant="body2" fontWeight="medium">
                    Make
                  </Typography>
                  <Typography variant="body1">
                    {workOrder.aircraft.make || 'Not specified'}
                  </Typography>
                </div>
                <div>
                  <Typography variant="body2" fontWeight="medium">
                    Serial Number
                  </Typography>
                  <Typography variant="body1">
                    {workOrder.aircraft.serialNumber || 'Not specified'}
                  </Typography>
                </div>
                <div>
                  <Typography variant="body2" fontWeight="medium">
                    Description
                  </Typography>
                  <Typography variant="body1">
                    {workOrder.aircraft.description || 'No description'}
                  </Typography>
                </div>
              </div>
            </div>

            {/* Components Section */}
            <div>
              <Typography variant="h6" gutterBottom>
                Components
              </Typography>
              {workOrder.aircraft.components &&
              workOrder.aircraft.components.length > 0 ? (
                <div className="space-y-3">
                  {workOrder.aircraft.components.map((component) => (
                    <Box
                      key={component.id}
                      p={3}
                      border={1}
                      borderColor="grey.300"
                      borderRadius={1}
                      className="bg-gray-50"
                    >
                      <Typography
                        variant="body1"
                        fontWeight="medium"
                        gutterBottom
                      >
                        {component.name}
                      </Typography>
                      <div className="space-y-1">
                        <Typography variant="body2" color="textSecondary">
                          Part Number: {component.partNumber || 'N/A'}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Serial Number: {component.serialNumber || 'N/A'}
                        </Typography>
                      </div>
                    </Box>
                  ))}
                </div>
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No components found
                </Typography>
              )}
            </div>
          </div>
        </div>

        {/* Footer - Close Button */}
        <div className="border-t bg-gray-50 p-4">
          <div className="flex justify-end">
            <Button
              variant="outlined"
              onClick={() => setAircraftDetailsOpen(false)}
            >
              Close
            </Button>
          </div>
        </div>
      </Drawer>

      {/* External Aircraft Time Modal */}
      <ExternalAircraftTimeModal
        open={applyTimesOpen}
        onClose={() => setApplyTimesOpen(false)}
        aircraft={workOrder.aircraft}
        onSuccess={async (usageLogId) => {
          console.log('Modal onSuccess called with usageLogId:', usageLogId)
          console.log('Work order ID:', workOrder.id)
          try {
            // Update the work order with the usage log ID
            await updateWorkOrder({
              variables: {
                id: workOrder.id,
                input: {
                  usageLogId,
                },
              },
            })

            // Don't call refetch() here - it causes infinite re-renders
            // The mutation should update the cache automatically
            toast.success('Aircraft times updated successfully')
            setApplyTimesOpen(false)
          } catch (error) {
            console.error('Error updating work order:', error)
            toast.error(
              `Failed to link usage log to work order: ${error.message}`
            )
          }
        }}
      />

      {/* Review Modal */}
      <Dialog
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Review Work Order</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary">
            Review functionality coming soon...
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ExternalWorkOrderDetailPage
